import { useResizeObserver } from '@orn/shared-web/hooks/useResizeObserver';
import { cx } from '@orn/shared-web/utils/cx';
import formatRangeEndAmount from '@orn/shared-web/utils/money/formatRangeEndAmount';
import * as React from 'react';

import { formatMarkerAmount } from './amountLabels';
import {
  AMOUNT_RANGE_BAR_HEIGHT_PX,
  isRenderableRange,
  resolveAmountRangeBarLayout,
  type IAmountRangeBarMetrics,
} from './layout';

const MEDIAN_PREFIX = 'Median: ';
const CURRENT_PREFIX = 'Current: ';

function markerStyle(ratio: number, shift = 0): React.CSSProperties {
  return { left: `${ratio * 100}%`, transform: `translateX(calc(-50% + ${shift}px))` };
}

function sameMetrics(a: IAmountRangeBarMetrics, b: IAmountRangeBarMetrics): boolean {
  return (Object.keys(a) as Array<keyof IAmountRangeBarMetrics>).every((key) => a[key] === b[key]);
}

export interface IAmountRangeBarProps {
  /** Smallest amount in the range. All four amounts are same-sign magnitudes on one scale. */
  min: number;
  max: number;
  median: number;
  /** The record's own amount. It may fall outside `[min, max]`; the dot then clamps to the track end. */
  current: number;
  /** ISO 4217 code for amounts not in the household currency; omitted means USD. */
  currencyCode?: string | null;
  className?: string;
}

/**
 * Horizontal min → max scale with a median tick above the track and the current amount's dot below.
 *
 * Renders nothing for a non-finite or inverted range. Height is fixed at `AMOUNT_RANGE_BAR_HEIGHT_PX`
 * so a consumer can stack rows straight beneath it; narrow widths degrade the labels instead.
 */
const AmountRangeBar = (props: IAmountRangeBarProps) => {
  // The scale mounts only for a drawable range, so its observers always attach to live elements.
  return isRenderableRange(props) ? <AmountRangeBarScale {...props} /> : null;
};

function AmountRangeBarScale({ min, max, median, current, currencyCode, className }: IAmountRangeBarProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const minLabelRef = React.useRef<HTMLSpanElement>(null);
  const maxLabelRef = React.useRef<HTMLSpanElement>(null);
  const medianPrefixRulerRef = React.useRef<HTMLSpanElement>(null);
  const medianValueRef = React.useRef<HTMLSpanElement>(null);
  const currentLabelRef = React.useRef<HTMLSpanElement>(null);
  const [metrics, setMetrics] = React.useState<IAmountRangeBarMetrics | null>(null);

  const measure = React.useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const next: IAmountRangeBarMetrics = {
      containerWidth: container.offsetWidth,
      minLabelWidth: minLabelRef.current?.offsetWidth ?? 0,
      maxLabelWidth: maxLabelRef.current?.offsetWidth ?? 0,
      medianPrefixWidth: medianPrefixRulerRef.current?.offsetWidth ?? 0,
      medianValueWidth: medianValueRef.current?.offsetWidth ?? 0,
      currentLabelWidth: currentLabelRef.current?.offsetWidth ?? 0,
    };
    setMetrics((previous) => (previous && sameMetrics(previous, next) ? previous : next));
  }, []);

  // Layout effect so the first paint already reflects real label widths; observers cover resizes and font swaps.
  React.useLayoutEffect(() => {
    measure();
  }, [measure, min, max, median, current, currencyCode]);
  useResizeObserver({ ref: containerRef, onResize: measure });
  useResizeObserver({ ref: medianValueRef, onResize: measure });
  useResizeObserver({ ref: currentLabelRef, onResize: measure });

  const layout = resolveAmountRangeBarLayout({ min, max, median, current }, metrics);
  const minAmount = formatRangeEndAmount(min, currencyCode);
  const maxAmount = formatRangeEndAmount(max, currencyCode);
  const medianAmount = formatMarkerAmount(median, currencyCode);
  const currentAmount = formatMarkerAmount(current, currencyCode);
  const endLabelClassName = 'text-secondary text-md absolute top-[9px] leading-[22px] font-semibold whitespace-nowrap';

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label={`Amount range ${minAmount} to ${maxAmount}, median ${medianAmount}, current ${currentAmount}`}
      className={cx('relative w-full', className)}
      style={{ height: AMOUNT_RANGE_BAR_HEIGHT_PX }}
    >
      {/* Hidden labels stay mounted so their widths keep feeding the layout; unmounting them would flip it back. */}
      <span ref={minLabelRef} className={cx(endLabelClassName, 'left-0', !layout.showMinLabel && 'invisible')}>
        {minAmount}
      </span>
      <span ref={maxLabelRef} className={cx(endLabelClassName, 'right-0', !layout.showMaxLabel && 'invisible')}>
        {maxAmount}
      </span>

      <div className="bg-quaternary absolute inset-x-0 top-[46px] h-[7px] rounded-full" />

      <div
        className="bg-fg-brand-secondary absolute top-[27px] h-[26px] w-[2px] rounded-full"
        style={markerStyle(layout.medianRatio)}
      />
      <span
        className="text-fg-brand-secondary text-md absolute top-0 whitespace-nowrap font-semibold leading-[22px]"
        style={markerStyle(layout.medianRatio, layout.medianLabelShift)}
      >
        <span ref={medianPrefixRulerRef} className="invisible absolute whitespace-pre">
          {MEDIAN_PREFIX}
        </span>
        {layout.showMedianPrefix && MEDIAN_PREFIX}
        <span ref={medianValueRef}>{medianAmount}</span>
      </span>

      <div
        className="bg-fg-success-primary absolute top-[40px] flex size-4 items-center justify-center rounded-full"
        style={markerStyle(layout.currentRatio)}
      >
        <div className="bg-primary size-1.5 rounded-full" />
      </div>
      <span
        ref={currentLabelRef}
        data-testid="amount-range-bar-current-label"
        className="text-fg-success-primary text-md absolute top-[65px] whitespace-nowrap font-semibold leading-[22px]"
        style={markerStyle(layout.currentRatio, layout.currentLabelShift)}
      >
        {CURRENT_PREFIX}
        {currentAmount}
      </span>
    </div>
  );
}

export default AmountRangeBar;
